/**
 * SaveIndicator — pastille discrète fixe (en haut à droite) d'enregistrement automatique.
 * Écoute l'évènement global `vd-save` (émis par le client API et par useFormAutoSave).
 * États : « Enregistrement… » (gris pulsé), « Enregistrement fait » (vert, fondu 2 s),
 * « Hors ligne – non enregistré » (orange, persistant), « Modifié ailleurs, recharger » (persistant).
 * Montée une seule fois dans le layout.
 */
import { useEffect, useRef, useState } from 'react';
import { Check, AlertTriangle, WifiOff } from 'lucide-react';

export function SaveIndicator() {
  const [state, setState] = useState('idle'); // idle | saving | saved | offline | conflict
  const pending = useRef(0);
  const hideTimer = useRef(null);

  useEffect(() => {
    const onSave = (e) => {
      const s = e.detail?.state;
      clearTimeout(hideTimer.current);
      if (s === 'saving') { pending.current += 1; setState('saving'); return; }
      if (s === 'offline' || s === 'error') { pending.current = Math.max(0, pending.current - 1); setState('offline'); return; } // persistant
      if (s === 'conflict') { pending.current = Math.max(0, pending.current - 1); setState('conflict'); return; } // persistant
      // saved
      pending.current = Math.max(0, pending.current - 1);
      if (pending.current === 0) {
        setState('saved');
        hideTimer.current = setTimeout(() => setState('idle'), 2000);
      }
    };
    const onOffline = () => setState((st) => (st === 'saving' || st === 'offline' ? 'offline' : st));
    window.addEventListener('vd-save', onSave);
    window.addEventListener('offline', onOffline);
    return () => { window.removeEventListener('vd-save', onSave); window.removeEventListener('offline', onOffline); clearTimeout(hideTimer.current); };
  }, []);

  if (state === 'idle') return null;
  const map = {
    saving: { dot: 'bg-n500 animate-pulse', text: 'Enregistrement…', icon: null, cls: 'text-n700' },
    saved: { dot: 'bg-green-600', text: 'Enregistrement fait', icon: Check, cls: 'text-green-700' },
    offline: { dot: 'bg-orange-500', text: 'Hors ligne – non enregistré', icon: WifiOff, cls: 'text-orange-700' },
    conflict: { dot: 'bg-orange-500', text: 'Modifié ailleurs, recharger', icon: AlertTriangle, cls: 'text-orange-700' },
  }[state];
  const Icon = map.icon;
  return (
    <div
      data-testid="save-indicator" data-state={state}
      className="fixed bottom-4 right-4 z-[60] flex items-center gap-2 bg-paper border border-hair rounded-full shadow-sm px-3 py-1.5 text-[12px] animate-fade-in">
      <span className={`w-2 h-2 rounded-full ${map.dot}`} />
      {Icon && <Icon className={`w-3.5 h-3.5 ${map.cls}`} strokeWidth={1.8} />}
      <span className={map.cls}>{map.text}</span>
    </div>
  );
}
